import { useCallback } from "react"
import { useProdutoApi } from "../../infra/produto"
import { useAppDispatch, useAppSelector } from "../../presentation/hooks/store-hooks"
import { IModuleInfo, setCurrentUserModulesState } from "../../application/states/user"
import { setProdutosTipoConsumoState } from "../../application/states/produto"
import { useUIService } from "../ui"
import { IProduto } from "../../application/models"
import {
    setVincularProdutoCreateClienteFormFieldsState,
    removeProdutoCreateClienteFormFieldsState
} from "../../application/states/clientes"


export const useProdutoService = () => {
    // dispatch
    const dispatch = useAppDispatch()
    // State
    const { currentUser } = useAppSelector((state) => state.user)
    const { produtosTipoConsumo } = useAppSelector((state) => state.produto)
    const { createClienteForm } = useAppSelector((state) => state.clientes)
    // Infra
    const { getProdutosByIdUsuarioApi } = useProdutoApi()
    // Service
    const { setAlert, setLoading } = useUIService()

    // Actions
    const getProdutosByIdUsuarioAndSetModules = useCallback(async () => {
        if (currentUser) {
            const loadingId = Math.random().toString()
            try {
                setLoading({
                    id: loadingId,
                    loading: true,
                    message: 'Buscando módulos do usuário...'
                })

                // Busca produtos por usuário (controle de acesso - sem fallback)
                // IMPORTANTE: Não fazer fallback para produtos do cliente por questões de segurança
                // Se o usuário não tem produtos atribuídos via perfil, não deve ver nenhum módulo
                const produtos: IProduto[] = await getProdutosByIdUsuarioApi({
                    IdUsuario: currentUser.IdUsuario
                })


                const moduleInfo: IModuleInfo[] = produtos.map((produto) => {
                    return {
                        IdProduto: produto.IdProduto,
                        Nome: produto.Descricao
                    }
                })

                dispatch(setCurrentUserModulesState(moduleInfo))
            } catch (error: any) {
                const errorMessage = error.response?.data?.error ||
                    error.response?.data?.message ||
                    error.message ||
                    'Erro ao buscar produtos do usuário. Por favor, tente novamente.'



                setAlert({
                    message: errorMessage,
                    type: 'error'
                })
            } finally {
                setLoading({
                    id: loadingId,
                    loading: false
                })
            }
        }
    }, [currentUser, dispatch, getProdutosByIdUsuarioApi, setAlert, setLoading])


    const vincularProdutoCreateClienteForm = useCallback((idProduto: number) => {
        dispatch(setVincularProdutoCreateClienteFormFieldsState({ idProduto }))
    }, [dispatch])

    const removerProdutoCreateClienteForm = useCallback((idProduto: number) => {
        dispatch(removeProdutoCreateClienteFormFieldsState({ idProduto }))
    }, [dispatch])


    const validateProdutosObrigatorios = useCallback((): { isValid: boolean; errorMessage?: string } => {
        if (!createClienteForm.fields.ProdutosTiposConsumos || createClienteForm.fields.ProdutosTiposConsumos.length === 0) {
            return {
                isValid: false,
                errorMessage: "É obrigatório selecionar pelo menos um produto"
            }
        }
        return { isValid: true }
    }, [createClienteForm.fields.ProdutosTiposConsumos])


    const validateProdutosForm = useCallback((): { isValid: boolean; errorMessage?: string } => {
        const validacaoObrigatorios = validateProdutosObrigatorios()
        if (!validacaoObrigatorios.isValid) {
            return validacaoObrigatorios
        }

        return { isValid: true }
    }, [validateProdutosObrigatorios])

    const obterProdutos = useCallback((): Array<{
        IDPRODUTO: number;
        NomeProduto: string;
        Descricao: string;
    }> => {
        if (produtosTipoConsumo && Array.isArray(produtosTipoConsumo) && produtosTipoConsumo.length > 0) {
            const produtosMap = new Map<number, {
                IDPRODUTO: number;
                NomeProduto: string;
                Descricao: string;
            }>();

            produtosTipoConsumo.forEach((item: any) => {
                if (item?.produto?.IdProduto) {
                    if (!produtosMap.has(item.produto.IdProduto)) {
                        produtosMap.set(item.produto.IdProduto, {
                            IDPRODUTO: item.produto.IdProduto,
                            NomeProduto: item.produto.Descricao || item.produto.NomeProduto || 'Produto sem nome',
                            Descricao: item.produto.Descricao || item.produto.DescricaoProduto || ''
                        });
                    }
                } else if (item?.IdProduto) {
                    if (!produtosMap.has(item.IdProduto)) {
                        produtosMap.set(item.IdProduto, {
                            IDPRODUTO: item.IdProduto,
                            NomeProduto: item.Descricao || item.NomeProduto || 'Produto sem nome',
                            Descricao: item.Descricao || item.DescricaoProduto || ''
                        });
                    }
                }
            });

            const produtos = Array.from(produtosMap.values());

            const todosProdutos = {
                IDPRODUTO: 0,
                NomeProduto: 'Todos os Produtos',
                Descricao: 'Selecionar todos os produtos disponíveis'
            };

            return [todosProdutos, ...produtos];
        }

        return [{
            IDPRODUTO: 0,
            NomeProduto: 'Nenhum produto disponível',
            Descricao: 'Não há produtos cadastrados'
        }];
    }, [produtosTipoConsumo]);

    const getProdutosSelecionados = useCallback(() => {
        return createClienteForm.fields.ProdutosTiposConsumos || []
    }, [createClienteForm.fields.ProdutosTiposConsumos])

    const isProdutoSelecionado = useCallback((idProduto: number): boolean => {
        return createClienteForm.fields.ProdutosTiposConsumos?.some(
            produto => produto.IdProduto === idProduto
        ) || false
    }, [createClienteForm.fields.ProdutosTiposConsumos])


    const getQuantidadeProdutosSelecionados = useCallback((): number => {
        return createClienteForm.fields.ProdutosTiposConsumos?.length || 0
    }, [createClienteForm.fields.ProdutosTiposConsumos])

    return {
        getProdutosByIdUsuarioAndSetModules,

        // FUNÇÕES PARA FORMULÁRIO
        vincularProdutoCreateClienteForm,
        removerProdutoCreateClienteForm,

        // VALIDAÇÕES DE PRODUTOS
        validateProdutosForm,
        validateProdutosObrigatorios,

        // UTILITÁRIOS
        obterProdutos,
        getProdutosSelecionados,
        isProdutoSelecionado,
        getQuantidadeProdutosSelecionados
    }
}